import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { getInputPlaceholderPlugin } from '@lyearn/lygo-blocks';
import { EditorState, EditorView } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import { Typography } from '@lyearn/ui';
import Editor from '@/components/PageEditor';
import useUserAwareness from '@/hooks/useUserAwareness';
import useYWebsocketProvider from '@/hooks/useYWebsocketProvider';

import { editorStyles } from './styles';
import { NotesTabProps } from './types';
import { getNotesEditorSchema } from './utils';

const NotesTab = ({
  rootBlockId,
  editorParams,
  LoaderComponent,
  className,
  onWebsocketDisconnect,
}: NotesTabProps) => {
  useTranslation(['Common', 'Lygo']);

  const { t } = useTranslation();

  const notesEditorProps = getNotesEditorSchema(t);

  const yEditorProvider = useYWebsocketProvider(
    notesEditorProps,
    rootBlockId,
    editorParams,
    undefined,
    onWebsocketDisconnect,
  );
  useUserAwareness(yEditorProvider!);

  const onCreateEditorView = useCallback(
    (view: EditorView) => {
      view.updateState(
        EditorState.create({
          schema: notesEditorProps.schema,
          plugins: [...getInputPlaceholderPlugin(t('Common:EmptyNotes')), ...view.state.plugins],
        }),
      );
      view.focus();
    },
    [notesEditorProps.schema, t],
  );

  return (
    <Typography
      as="div"
      className={cn(editorStyles, 'cursor-text', className)}
      color="text-text-paragraph"
      variant="body-short-02">
      <Editor
        LoaderComponent={LoaderComponent}
        {...notesEditorProps}
        editable
        yProvider={yEditorProvider!}
        onCreateEditorView={onCreateEditorView}
      />
    </Typography>
  );
};

export default React.memo(NotesTab);
